import React, { useState } from 'react';
import BookingService from '../../Services/BookingService';
import BillingoService from '../../Services/BillingoService';
import BillingoDocumentModal from './BillingoDocumentModal';

/**
 * Opens the create-document flow for a group of bookings — THE way into it
 * for every launcher (the Invoicing queues, an order's row in the Orders
 * table, a recorded payment), so none of them opens it blind.
 *
 * Every order in the group is loaded before the flow opens, and the group's
 * existing documents are fetched in ONE request: the flow then recommends the
 * right next document, shows what is already invoiced, and deducts a shared
 * Előlegszámla exactly once (Billingo `advance_invoice`). A group that cannot
 * be loaded completely is refused: issuing on part of it would split the
 * advance across documents.
 *
 * `group` is `{ group_key, booking_ids }`, plus `payment` when the document is
 * for money received (the flow then assigns that payment to the order), and
 * an optional `skip(orders, existingDocs)` for a launcher that only wants the
 * flow when something is still owed.
 *
 * Returns `issue(group)`, the key of the group being prepared, the last error,
 * and the modal element to render.
 */
export default function useDocumentIssuer({ onIssued = () => {} } = {}) {
  const [preparing, setPreparing] = useState(null);
  const [error, setError] = useState('');
  const [flowContext, setFlowContext] = useState(null);

  const issue = async (group) => {
    setPreparing(group.group_key);
    setError('');
    try {
      const orders = (await Promise.all(
        group.booking_ids.map((id) => BookingService.getBookingById(id)),
      )).filter(Boolean);
      if (orders.length === 0) throw new Error('The booking could not be loaded.');
      if (orders.length !== group.booking_ids.length) {
        throw new Error(
          `Only ${orders.length} of ${group.booking_ids.length} orders in this group could be `
          + 'loaded. Issuing now would split the advance across documents.',
        );
      }
      const existingDocs = await BillingoService.listDocumentsForBookings(orders);
      if (group.skip?.(orders, existingDocs)) return;
      setFlowContext({
        mode: group.payment ? 'payment' : 'order',
        bookings: orders,
        order: orders[0],
        partner: orders[0].partner,
        existingDocs,
        ...(group.payment ? { payment: group.payment } : {}),
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not open the document flow.');
    } finally {
      setPreparing(null);
    }
  };

  const modal = (
    <BillingoDocumentModal
      show={!!flowContext}
      context={flowContext || {}}
      onHide={() => setFlowContext(null)}
      onComplete={() => { setFlowContext(null); onIssued(); }}
    />
  );

  return {
    issue, preparing, error, setError, modal,
  };
}
